'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { ButtonSize, ButtonVariant } from '~/ui/components/button';
import { Pill } from '~/ui/components/pill';
import { type FilterOption } from '~/ui/systems/filter';

import styles from './filterPills.module.scss';

interface FilterPill {
  id: string;
  queryParamName: string;
  name: string;
}

export interface FilterPillsProps {
  selectedFilterOptions: Record<string, FilterOption[]>;
}

export function FilterPills({ selectedFilterOptions }: FilterPillsProps): React.ReactElement {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const pills: FilterPill[] = Object.entries(selectedFilterOptions).flatMap(([name, options]) =>
    options.map(option => ({
      id: option.id,
      queryParamName: name,
      name: option.name,
    })),
  );

  function getPillRemovedUrl(pill: FilterPill) {
    const params = new URLSearchParams(searchParams?.toString());
    params.delete(pill.queryParamName, pill.name);
    return `${pathname}?${params}`;
  }

  function getAllPillsRemovedUrl() {
    const params = new URLSearchParams(searchParams?.toString());
    pills.forEach(pill => params.delete(pill.queryParamName, pill.name));
    return `${pathname}?${params}`;
  }

  return (
    <>
      {pills.length ? (
        <div className={styles.pills}>
          <ul className={styles.pillsContainer} aria-label="Active filters" aria-live="polite">
            {pills.map(pill => (
              <Pill
                key={pill.id}
                label={pill.name}
                size={ButtonSize.TINY}
                variant={ButtonVariant.THEME}
                onClick={() => router.replace(getPillRemovedUrl(pill), { scroll: false })}
                onMouseEnter={() => router.prefetch(getPillRemovedUrl(pill))}
              />
            ))}
            {pills.length > 2 && (
              <Pill
                size={ButtonSize.TINY}
                variant={ButtonVariant.SECONDARY}
                label="Clear all"
                onClick={() => router.replace(getAllPillsRemovedUrl(), { scroll: false })}
                onMouseEnter={() => router.prefetch(getAllPillsRemovedUrl())}
              />
            )}
          </ul>
        </div>
      ) : null}
    </>
  );
}
